
import React, { useState } from "react";
import { 
  Card, 
  CardHeader, 
  CardTitle, 
  CardContent, 
  CardDescription 
} from "@/components/ui/card";
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle, 
  DialogTrigger 
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from "@/components/ui/select";
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from "@/components/ui/table";
import { Label } from "@/components/ui/label";
import { formatDate } from "@/lib/utils";
import { ContactInteraction } from "@/types/custom-types";
import { useRelationshipHealth } from "@/hooks/useRelationshipHealth";
import { useAuth } from "@/contexts/AuthContext";
import { CalendarDays, MessageCircle, PhoneCall, Video, Users, Plus } from "lucide-react";

interface RelationshipInteractionsProps {
  contactId: string;
}

export function RelationshipInteractions({ contactId }: RelationshipInteractionsProps) {
  const { user } = useAuth();
  const { getContactInteractions, createInteraction, isCreatingInteraction } = useRelationshipHealth();
  const { data: interactions = [], isLoading } = getContactInteractions(contactId);
  
  const [newInteraction, setNewInteraction] = useState({
    type: "call",
    notes: "",
    sentiment: "neutral"
  });
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    createInteraction({
      contact_id: contactId,
      interaction_type: newInteraction.type,
      sentiment: newInteraction.sentiment,
      notes: newInteraction.notes,
      user_id: user?.id
    });
    
    // Reset form and close dialog
    setNewInteraction({
      type: "call",
      notes: "",
      sentiment: "neutral"
    });
    setIsDialogOpen(false);
  };

  const getInteractionIcon = (type: string) => {
    switch (type.toLowerCase()) {
      case 'call':
        return <PhoneCall className="h-4 w-4" />;
      case 'email':
        return <MessageCircle className="h-4 w-4" />;
      case 'meeting':
        return <Users className="h-4 w-4" />;
      case 'video call':
        return <Video className="h-4 w-4" />;
      default:
        return <CalendarDays className="h-4 w-4" />;
    }
  };

  const getSentimentClass = (sentiment: string | undefined) => {
    switch (sentiment?.toLowerCase()) {
      case 'positive':
        return "text-green-600";
      case 'negative':
        return "text-red-600";
      default:
        return "text-gray-600";
    }
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <div>
          <CardTitle>Interaction History</CardTitle>
          <CardDescription>
            Recent communication and meetings with this contact
          </CardDescription>
        </div>
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogTrigger asChild>
            <Button size="sm">
              <Plus className="h-4 w-4 mr-1" />
              Log Interaction
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Log New Interaction</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="type">Type of Interaction</Label>
                  <Select
                    value={newInteraction.type}
                    onValueChange={(value) => setNewInteraction({...newInteraction, type: value})}
                  >
                    <SelectTrigger id="type">
                      <SelectValue placeholder="Select type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="call">Phone Call</SelectItem>
                      <SelectItem value="email">Email</SelectItem>
                      <SelectItem value="meeting">In-person Meeting</SelectItem>
                      <SelectItem value="video call">Video Call</SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="sentiment">Sentiment</Label>
                  <Select
                    value={newInteraction.sentiment}
                    onValueChange={(value) => setNewInteraction({...newInteraction, sentiment: value})}
                  >
                    <SelectTrigger id="sentiment">
                      <SelectValue placeholder="Select sentiment" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="positive">Positive</SelectItem>
                      <SelectItem value="neutral">Neutral</SelectItem>
                      <SelectItem value="negative">Negative</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="notes">Notes</Label>
                <Textarea
                  id="notes"
                  value={newInteraction.notes}
                  onChange={(e) => setNewInteraction({...newInteraction, notes: e.target.value})}
                  placeholder="Enter details about the interaction"
                  rows={4}
                />
              </div>

              <div className="flex justify-end gap-2">
                <Button 
                  type="button" 
                  variant="outline" 
                  onClick={() => setIsDialogOpen(false)}
                >
                  Cancel
                </Button>
                <Button 
                  type="submit"
                  disabled={isCreatingInteraction}
                >
                  {isCreatingInteraction ? "Saving..." : "Save Interaction"}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="text-center py-4">Loading interactions...</div>
        ) : interactions.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            <MessageCircle className="mx-auto h-8 w-8 opacity-50" />
            <p className="mt-2">No interactions recorded yet</p>
            <Button 
              variant="outline" 
              className="mt-4"
              onClick={() => setIsDialogOpen(true)}
            >
              <Plus className="h-4 w-4 mr-1" />
              Log First Interaction
            </Button>
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Date</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Sentiment</TableHead>
                <TableHead>Notes</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {interactions.map((interaction) => (
                <TableRow key={interaction.id}>
                  <TableCell className="text-sm">
                    {formatDate(interaction.interaction_date)}
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1.5">
                      {getInteractionIcon(interaction.interaction_type)}
                      <span className="capitalize text-sm">
                        {interaction.interaction_type}
                      </span>
                    </div>
                  </TableCell>
                  <TableCell>
                    <span className={`capitalize text-sm ${getSentimentClass(interaction.sentiment)}`}>
                      {interaction.sentiment || 'Neutral'}
                    </span>
                  </TableCell>
                  <TableCell className="max-w-[300px] truncate text-sm">
                    {interaction.notes || '-'}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
}
